import React, { useEffect, useState } from 'react'
import ProductDetails from './ProductDetails';
import VariantDetails from './VariantDetails';
import ProductReview from './ProductReview';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { Product, Variant } from '../../../../types/Products';
import { ApiCreateProduct, ApiGetProducts, ApiUpdateProduct } from '../../../../Core/Apicall';
import { toast } from 'react-toastify';
import { useFormik, yupToFormErrors } from 'formik';
import * as Yup from "yup";


export const variantSchema = Yup.object({
  name: Yup.string().required("Variant name is required"),

  colorId: Yup.number().required("Color is required"),

  sizeId: Yup.number().required("Size is required"),

  price: Yup.number().required("Price is required"),

  discount_percentage: Yup.number()
    .min(0)
    .max(100),

  stock: Yup.number().required("Stock is required"),

  min_delivery_days: Yup.number().required(),

  max_delivery_days: Yup.number().required(),

  unit_text: Yup.string().required(),

  wholesalePriceTiers: Yup.array().of(
    Yup.object({
      min_quantity: Yup.number().required(),
      price: Yup.number().required(),
    })
  ),

  technicalDetails: Yup.array().of(
    Yup.object({
      key: Yup.string().required(),
      value: Yup.string().required(),
    })
  ),

  images: Yup.array()
    .of(
      Yup.object({
        image_url: Yup.string().required(),
        alt_text: Yup.string(),
        is_thumbnail: Yup.boolean(),
      })
    )
    .min(1, "At least one image is required"),
});

export const productSchema = Yup.object({
  categoryId: Yup.number().required("Category is required"),

  productName: Yup.string().required("Product name is required"),

  description: Yup.string().required("Description is required"),

  base_price: Yup.number()
    .required("Base price is required")
    .min(1),

  unit_text: Yup.string().required("Unit is required"),

  min_delivery_days: Yup.number().required(),

  max_delivery_days: Yup.number()
    .required()
    .min(
      Yup.ref("min_delivery_days"),
      "Must be greater than minimum delivery days"
    ),

  discount_percentage: Yup.number()
    .min(0)
    .max(100),

  currency: Yup.string().required(),

  wholesalePriceTiers: Yup.array()
    .of(
      Yup.object({
        min_quantity: Yup.number().required(),
        price: Yup.number().required(),
      })
    )
    .min(1),

  technicalDetails: Yup.array()
    .of(
      Yup.object({
        key: Yup.string().required(),
        value: Yup.string().required(),
      })
    )
    .min(1),

  media: Yup.array()
    .of(
      Yup.object({
        media_url: Yup.string().required(),
        media_type: Yup.string().required(),
      })
    )
    .min(1, "At least one image is required"),

  // variants: Yup.array()
  //   .of(
  //     Yup.object({
  //       name: Yup.string().required(),
  //       colorId: Yup.number().required(),

  //       sizeId: Yup.number().required(),

  //       price: Yup.number().required(),

  //       discount_percentage: Yup.number().min(0).max(100),

  //       stock: Yup.number().required(),

  //       min_delivery_days: Yup.number().required(),

  //       max_delivery_days: Yup.number().required(),

  //       unit_text: Yup.string().required(),

  //       wholesalePriceTiers: Yup.array().of(
  //         Yup.object({
  //           min_quantity: Yup.number().required(),
  //           price: Yup.number().required(),
  //         })
  //       ),

  //       technicalDetails: Yup.array().of(
  //         Yup.object({
  //           key: Yup.string().required(),
  //           value: Yup.string().required(),
  //         })
  //       ),

  //       images: Yup.array()
  //         .of(
  //           Yup.object({
  //             image_url: Yup.string().required(),
  //             alt_text: Yup.string(),
  //             is_thumbnail: Yup.boolean(),
  //           })
  //         )
  //         .min(1),
  //     })
  //   )
  //   .min(1),
});

const variantsSchema = Yup.object({
  variants: Yup.array()
    .of(variantSchema)
    .min(1, "At least one variant is required")
    .required("At least one variant is required"),
});

function AddorUpdateProduct() {
  const [activeStep, setActiveStep] = useState<number>(0)
  const steps = ['Product Details', "Variants", "Review"]
  const navigate = useNavigate()
  const { productId } = useParams();
  const [productDetails, setProductDetails] = useState<Product | null>()
  const isEditMode = !!productId;
  const fullSchema = productSchema;

  const getproductDetails = async () => {
    setProductDetails(null)
    try {
      const data = `?productId=${productId}`
      const res = await ApiGetProducts(data)
      if (res?.status) {
        setProductDetails(res?.data[0])
      }

    } catch (error: any) {
      toast.error(error?.message || "Something went wrong")
    }
  }

  useEffect(() => {
    productId && getproductDetails()
  }, [productId])
  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      categoryId: productDetails?.category.categoryId || "",

      productName: productDetails?.productName || "",

      description: productDetails?.description || "",

      base_price: productDetails?.base_price || "",

      unit_text: productDetails?.unit_text || "",

      min_delivery_days: productDetails?.min_delivery_days || "",

      max_delivery_days: productDetails?.max_delivery_days || "",

      discount_percentage: productDetails?.discount_percentage || "",

      discounted_price: productDetails?.discounted_price || "",

      currency: productDetails?.currency || "INR",

      wholesalePriceTiers:
        productDetails?.wholesalePriceTiers || [
          {
            min_quantity: "",
            price: "",
          },
        ],

      technicalDetails:
        productDetails?.technicalDetails || [
          {
            key: "",
            value: "",
          },
        ],

      media:
        productDetails?.media || [],

      variants:
        productDetails?.variants?.map((variant: Variant) => ({
          variantId: variant.variantId,

          name: variant.name || "",

          sku: variant.sku || "",

          colorId: variant.color?.colorId || "",

          sizeId: variant.size?.sizeId || "",

          price: Number(variant.price) || "",

          discount_percentage: Number(variant.discount_percentage) || "",

          discounted_price: Number(variant.discounted_price) || "",

          stock: variant.stock || "",

          unit_text: variant.unit_text || "",

          min_delivery_days: variant.min_delivery_days || "",

          max_delivery_days: variant.max_delivery_days || "",

          wholesalePriceTiers:
            variant.wholesalePriceTiers?.map((tier: any) => ({
              tierId: tier.tierId,
              min_quantity: tier.min_quantity,
              price: Number(tier.price),
            })) || [
              {
                min_quantity: "",
                price: "",
              },
            ],

          technicalDetails:
            variant.technicalDetails?.map((detail: any) => ({
              technicalDetailId: detail.technicalDetailId,
              key: detail.key,
              value: detail.value,
            })) || [
              {
                key: "",
                value: "",
              },
            ],

          images:
            variant.variantImages?.map((img: any) => ({
              variantImageId: img.variantImageId,
              image_url: img.image_url,
              alt_text: img.alt_text || "",
              is_thumbnail: img.is_thumbnail,
            })) || [],
        })) || [],
    },
    // validationSchema: fullSchema,

    onSubmit: async (values) => {
      try {
        const params = `?productId=${productId}`
        const data = {
          categoryId: Number(values.categoryId),

          productName: values.productName,

          description: values.description,

          base_price: Number(values.base_price),

          unit_text: values.unit_text,

          min_delivery_days: Number(values.min_delivery_days),

          max_delivery_days: Number(values.max_delivery_days),

          discount_percentage: Number(values.discount_percentage),

          currency: values.currency,

          wholesalePriceTiers: values.wholesalePriceTiers.map((tier: any) => ({
            tierId: tier?.tierId,
            min_quantity: Number(tier.min_quantity),
            price: Number(tier.price),
          })),

          technicalDetails: values.technicalDetails,

          media: values.media,

          variants: values.variants.map((variant: any) => ({
            name: variant.name,

            ...(variant.variantId && { variantId: variant.variantId }),
            ...(variant.variantId && { sku: variant.sku }),

            colorId: Number(variant.colorId),

            sizeId: Number(variant.sizeId),

            price: Number(variant.price),

            discount_percentage: Number(variant.discount_percentage),

            stock: Number(variant.stock),

            min_delivery_days: Number(variant.min_delivery_days),

            max_delivery_days: Number(variant.max_delivery_days),

            unit_text: variant.unit_text,

            wholesalePriceTiers: variant.wholesalePriceTiers.map((tier: any) => ({
              tierId: tier?.tierId,
              min_quantity: Number(tier.min_quantity),
              price: Number(tier.price),
            })),

            technicalDetails: variant.technicalDetails,

            images: variant.images,
          })),
        };


        if (isEditMode) {
          const res = await ApiUpdateProduct(params, data);
          if (res?.status) {
            formik.resetForm()
            navigate(-1)
          }

        } else {
          const res = await ApiCreateProduct(data);
          if (res?.status) {
            formik.resetForm()
            navigate(-1)
          }
        }

        // navigate(-1);
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    },
  });
  const validationSchemas = [
    productSchema,
    variantsSchema,
    null,
  ];

  const defaultVariant = {
    variantId: "",
    name: formik.values.productName,
    sku: "",
    colorId: "",
    sizeId: "",
    price: formik.values.base_price,
    discount_percentage: formik.values.discount_percentage,
    discounted_price: formik.values.discounted_price,
    stock: "",
    unit_text: formik.values.unit_text,
    min_delivery_days: formik.values.min_delivery_days,
    max_delivery_days: formik.values.max_delivery_days,
    wholesalePriceTiers: formik.values.wholesalePriceTiers,
    // [
    //   {
    //     min_quantity: ,
    //     price: "",
    //   },
    // ],
    technicalDetails: formik.values.technicalDetails,
    // [
    //   {
    //     key: "",
    //     value: "",
    //   },
    // ],
    images: [],
  };

  const handleNext = async () => {
    const schema = validationSchemas[activeStep];
    if (!schema) {
      formik.handleSubmit();
      return;
    }

    try {
      // await schema.validate(formik.values, {
      //   abortEarly: false,
      // });

      if (activeStep === 0) {
        if (formik.values.variants.length === 0) {
          formik.setFieldValue("variants", [defaultVariant]);
        }

        setActiveStep(1);
        return;
      }

      setActiveStep((prev) => prev + 1);
    } catch (err: any) {
      formik.setErrors(yupToFormErrors(err));

      err.inner.forEach((error: any) => {
        formik.setFieldTouched(error.path, true, false);
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const RenderStep = () => {
    switch (activeStep) {
      case 0:
        return <ProductDetails formik={formik} />;

      case 1:
        return <VariantDetails formik={formik} variantSchema={variantSchema} defaultVariant={defaultVariant} />;

      case 2:
        return <ProductReview formik={formik} />;

      default:
        return <ProductDetails formik={formik} />;
    }
  };
  // console.log("Errors", formik.errors)
  return (
    <Box className='p-3 stepper'>
      <Typography variant='h5' className='fw-bold'>Add New Product</Typography>

      <Stepper
        className='gap-5 mx-auto p-0 fs-20 position-relative'
        activeStep={activeStep}
        alternativeLabel
        sx={{
          width: {xs:"100%", md:"100%", xl:"50%"},
          "& .MuiStepLabel-label.Mui-active": {
            color: "var(--primary-color) !important",
            fontWeight: 600,
          },

          // Completed label
          "& .MuiStepLabel-label.Mui-completed": {
            color: "var( --primary-dark-color) !important",
            fontWeight: 600,
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {RenderStep()}

      <Box className='d-flex align-items-center justify-content-between mt-4'>
        <Button
          variant="outlined"
          className='btn-outlined-main'
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          Back
        </Button>

        <Button
          variant="contained"
          className='btn-main'
          onClick={handleNext}
          sx={{
            backgroundColor: "var(--primary-color)",
            "&:hover": {
              backgroundColor: "var(--primary-dark-color)",
            },
          }}
        >
          {activeStep === steps.length - 1 ? `${isEditMode ? "Update" : "Finish"}` : "Next"}
        </Button>
      </Box>
    </Box>
  )
}

export default AddorUpdateProduct
